import { TextButton, IconButton } from '@creditornot/cb-button';
import { Minus, Plus, Trashbin } from '@creditornot/cb-icons';
import React, { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import FileSelector from './FileSelector';
import { Crop, Cropper, CropPreview } from './ImageCropper';
import ImageWrapper from './ImageWrapper';
import { CROP_16_9 } from './constants';
import { CropperContainer, ZoomControls } from './styled';
import { useObjectUrl } from './utils';

type Props = {
  className?: string;
  crop?: Crop;
  file: File;
  onImageChange: (file: File) => void;
  onRemove: () => void;
  onCropChange: (crop: Crop) => void;
};

type State =
  | {
      type: 'initial';
    }
  | {
      type: 'cropping';
      crop: Crop;
    }
  | { type: 'selecting-file' };

const MAX_ZOOM = 2;
const ZOOM_STEP = 0.5;

const StyledCropper = styled(Cropper)``;

const Actions = styled.div`
  display: flex;
  margin-block-start: 16px;
  & > * + * {
    margin-inline-start: 16px;
  }
`;

const ImagePreview = styled.img`
  display: block;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  width: 100%;
`;

const NewImage = ({ className, crop, file, onCropChange, onImageChange, onRemove }: Props) => {
  const cropperRef = useRef<React.ElementRef<typeof Cropper>>(null);
  const [state, setState] = useState<State>({ type: 'initial' });

  const objectUrl = useObjectUrl(file);

  const renderState = () => {
    if (!objectUrl) {
      return null;
    }

    switch (state.type) {
      case 'initial': {
        return (
          <>
            <ImageWrapper>
              {crop ? <CropPreview crop={crop} imageUrl={objectUrl} /> : <ImagePreview src={objectUrl} />}
            </ImageWrapper>
            <Actions>
              <TextButton
                onClick={() => {
                  setState({ type: 'selecting-file' });
                }}
              >
                <FormattedMessage id="image-upload.change-image" />
              </TextButton>
              <TextButton
                onClick={() => {
                  setState({ type: 'cropping', crop: crop || CROP_16_9 });
                }}
              >
                <FormattedMessage id="image-upload.crop" />
              </TextButton>
              <TextButton icon={<Trashbin />} onClick={onRemove} variant="negative">
                <FormattedMessage id="image-upload.remove" />
              </TextButton>
            </Actions>
          </>
        );
      }
      case 'cropping': {
        return (
          <>
            <CropperContainer>
              <StyledCropper
                crop={state.crop}
                imageUrl={objectUrl}
                maxZoom={MAX_ZOOM}
                onCropChange={newCrop => {
                  setState({ type: 'cropping', crop: newCrop });
                }}
                ref={cropperRef}
              />
              <ZoomControls>
                <IconButton
                  background="solid"
                  disabled={state.crop.zoom >= MAX_ZOOM}
                  onClick={() => {
                    if (!cropperRef.current) {
                      return;
                    }
                    cropperRef.current.zoom(state.crop.zoom + ZOOM_STEP);
                  }}
                  size="tiny"
                  variant="neutral"
                >
                  <Plus />
                </IconButton>
                <IconButton
                  background="solid"
                  disabled={state.crop.zoom <= 1}
                  onClick={() => {
                    if (!cropperRef.current) {
                      return;
                    }
                    cropperRef.current.zoom(state.crop.zoom - ZOOM_STEP);
                  }}
                  size="tiny"
                  variant="neutral"
                >
                  <Minus />
                </IconButton>
              </ZoomControls>
            </CropperContainer>
            <Actions>
              <TextButton
                variant="grey"
                onClick={() => {
                  setState({ type: 'initial' });
                }}
              >
                <FormattedMessage id="image-upload.cancel" />
              </TextButton>
              <TextButton
                onClick={() => {
                  onCropChange(state.crop);
                  setState({ type: 'initial' });
                }}
              >
                <FormattedMessage id="image-upload.applyCrop" />
              </TextButton>
            </Actions>
          </>
        );
      }
      case 'selecting-file': {
        return (
          <FileSelector
            background={crop ? <CropPreview crop={crop} imageUrl={objectUrl} /> : <ImagePreview src={objectUrl} />}
            onCancel={() => {
              setState({ type: 'initial' });
            }}
            onSelectFile={file => {
              onImageChange(file);
              setState({ type: 'initial' });
            }}
          />
        );
      }
    }
  };

  return <div className={className}>{renderState()}</div>;
};

export default NewImage;
